// The review deck — one thing Oreia holds about you at a time, and a verdict on it.
//
// Why a deck and not the grid it replaces: a wall of twelve claims about your character is something
// you skim and then feel vaguely surveilled by. One card, with the entries it came from and three
// honest answers, is something you actually read. The portrait only gets more accurate if the "no"
// is as easy as the "yes", so "Not me" sits at the same size as "Fits" rather than hiding in a menu.
//
// Buttons are the interface; swipe is a shortcut on top of it. That ordering matters — a deck that
// can only be swiped is unusable with a keyboard, unreadable to a screen reader, and a mystery on a
// laptop, so the drag handler is an addition that no part of the flow depends on.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEntries } from '../entries/EntriesProvider';
import { useSession } from '../auth/SessionProvider';
import {
  fetchObservations,
  respondToObservation,
  type Observation,
} from '../api/portrait';
import { pendingCards, swipeVerdict, verdictPing, type VerdictAction } from './review-deck';
import { ReviewCard } from './ReviewCard';
import { DiscussSheet } from './DiscussSheet';
import { EmptyState, Spinner } from '../components/ui';
import { IconChevronRight } from '../components/Icons';
import { useToast, JournalToast } from '../journals/toast';

export function ReviewScreen() {
  const { entries } = useEntries();
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;
  const navigate = useNavigate();
  const { toast, ping } = useToast();

  const [queue, setQueue] = useState<Observation[] | null>(null);
  const [index, setIndex] = useState(0);
  const [discussing, setDiscussing] = useState<Observation | null>(null);
  /** Non-null only while a card is animating off. Also locks out a second verdict mid-flight. */
  const [leaving, setLeaving] = useState<VerdictAction | null>(null);
  const [drag, setDrag] = useState(0);

  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();
    fetchObservations(dek, controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        // Frozen on arrival. The deck is a fixed set of questions with an end you can see coming —
        // re-deriving it from live state would let a card you just answered reshuffle the queue.
        setQueue(pendingCards(all));
      })
      .catch(() => {
        if (!controller.signal.aborted) setQueue([]);
      });
    return () => controller.abort();
  }, [dek]);

  const entryTitle = useCallback(
    (id: string) => entries.find((e) => e.id === id)?.title?.trim() || null,
    [entries],
  );

  const cards = queue ?? [];
  const current = cards[index] ?? null;
  const next = cards[index + 1] ?? null;

  const settle = useCallback(
    (observation: Observation, action: VerdictAction) => {
      if (leaving) return;
      setLeaving(action);
      setDrag(0);
      ping(verdictPing(action));

      // Optimistic, and deliberately unawaited. A verdict is a preference, not a transaction: making
      // someone watch a spinner to say "that's not me" is the wrong price for the answer we most
      // want. A failure leaves the card pending, so it comes back tomorrow.
      void respondToObservation(observation.id, action).catch(() => {});

      timer.current = window.setTimeout(() => {
        setLeaving(null);
        setIndex((i) => i + 1);
      }, 240);
    },
    [leaving, ping],
  );

  // Left and right arrows read the two verdicts the swipe does, so the deck works without a mouse.
  useEffect(() => {
    if (!current || discussing) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'ArrowRight') settle(current!, 'affirm');
      else if (event.key === 'ArrowLeft') settle(current!, 'reject');
      else return;
      event.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, discussing, settle]);

  const remaining = Math.max(cards.length - index, 0);
  const answered = Math.min(index, cards.length);

  if (!dek || queue === null) {
    return (
      <div className="oscreen oreview">
        <Spinner label="Gathering what Oreia holds…" />
      </div>
    );
  }

  return (
    <div className="oscreen oreview">
      <div className="oreview-head">
        <Link className="oreview-back" to="/self">
          Self
        </Link>
        <h1 className="otitle">Is this you?</h1>
        <p className="obody">
          Oreia wrote these from your own entries. Say what fits and what doesn&apos;t — the no is
          the part that teaches it most.
        </p>
      </div>

      {current ? (
        <>
          <div className="oreview-progress" aria-hidden>
            {cards.map((card, i) => (
              <span key={card.id} className={`oreview-pip${i < index ? ' done' : i === index ? ' now' : ''}`} />
            ))}
          </div>
          <p className="omono oreview-count">
            {answered + 1} of {cards.length}
          </p>

          <ReviewDeckStack
            key={current.id}
            current={current}
            next={next}
            entryTitle={entryTitle}
            leaving={leaving}
            drag={drag}
            onDrag={setDrag}
            onVerdict={(action) => settle(current, action)}
            onDiscuss={() => setDiscussing(current)}
          />
        </>
      ) : (
        <EmptyState
          title={cards.length > 0 ? 'That’s all of them' : 'Nothing to review'}
          body={
            cards.length > 0
              ? `You went through ${cards.length} ${cards.length === 1 ? 'card' : 'cards'}. Oreia will write more as you keep journalling.`
              : 'Oreia has nothing waiting on your word. Keep writing and it will notice something.'
          }
        />
      )}

      <div className="oreview-foot">
        <Link className="oreview-archive" to="/self/archive">
          Everything Oreia has held
          <IconChevronRight />
        </Link>
        {!current && remaining === 0 && cards.length > 0 ? (
          <button className="oreview-done" onClick={() => navigate('/self')}>
            Back to your portrait
          </button>
        ) : null}
      </div>

      {discussing ? (
        <DiscussSheet
          observation={discussing}
          onClose={() => setDiscussing(null)}
          onResolved={(text) => {
            // Accepting a rewording affirms it server-side, so the card is settled. Show their
            // wording as it leaves rather than the sentence they just argued out of.
            setQueue((q) => (q ?? []).map((o) => (o.id === discussing.id ? { ...o, text } : o)));
            setDiscussing(null);
            ping('Saved in your words');
            setLeaving('affirm');
            timer.current = window.setTimeout(() => {
              setLeaving(null);
              setIndex((i) => i + 1);
            }, 240);
          }}
        />
      ) : null}

      <JournalToast text={toast} />
    </div>
  );
}

interface StackProps {
  current: Observation;
  next: Observation | null;
  entryTitle: (id: string) => string | null;
  leaving: VerdictAction | null;
  drag: number;
  onDrag(dx: number): void;
  onVerdict(action: VerdictAction): void;
  onDiscuss(): void;
}

/**
 * The card, the one behind it, and the drag.
 *
 * Pointer events rather than touch events, so a trackpad drag works the same as a thumb. The pointer
 * is captured once the movement is clearly a drag, which is what keeps a fast flick from being
 * dropped when it leaves the element mid-gesture — and, by waiting, keeps a plain click on a source
 * link a plain click.
 */
function ReviewDeckStack({
  current,
  next,
  entryTitle,
  leaving,
  drag,
  onDrag,
  onVerdict,
  onDiscuss,
}: StackProps) {
  /** Where the press began, and whether it has become a drag. */
  const start = useRef<number | null>(null);
  const held = useRef(false);

  const intent = useMemo(() => (leaving ? leaving : swipeVerdict(drag)), [leaving, drag]);

  const style = leaving
    ? undefined // the exit is a CSS animation; a transform here would fight it
    : { transform: `translateX(${drag}px) rotate(${drag * 0.035}deg)` };

  return (
    <div className="oreview-stack">
      {/* `inert` rather than aria-hidden: the peeked card carries source links, and hiding an element
          that still holds tab stops is how a keyboard lands on something a screen reader denies. */}
      {next ? (
        <div className="oreview-behind" inert>
          <ReviewCard observation={next} entryTitle={entryTitle} />
        </div>
      ) : null}

      <div
        className={`oreview-live${leaving ? ` is-leaving-${leaving}` : ''}${drag ? ' is-dragging' : ''}`}
        style={style}
        onPointerDown={(e) => {
          if (e.button !== 0 || leaving) return;
          start.current = e.clientX;
        }}
        onPointerMove={(e) => {
          if (start.current == null) return;
          const dx = e.clientX - start.current;
          // Capture only once this is unambiguously a drag. Capturing on press would retarget the
          // click to this container, and the card's source-entry links — the whole traceability
          // promise — would stop opening.
          if (!held.current && Math.abs(dx) > 6) {
            e.currentTarget.setPointerCapture(e.pointerId);
            held.current = true;
          }
          if (held.current) onDrag(dx);
        }}
        onPointerUp={(e) => {
          if (start.current == null) return;
          const dx = e.clientX - start.current;
          const dragged = held.current;
          start.current = null;
          held.current = false;
          if (!dragged) return;

          const verdict = swipeVerdict(dx);
          if (verdict) onVerdict(verdict);
          else onDrag(0);
        }}
        onPointerCancel={() => {
          start.current = null;
          held.current = false;
          onDrag(0);
        }}
      >
        {/* The verdict a release would give, shown while the drag is still reversible. */}
        <span className={`oreview-stamp fits${intent === 'affirm' ? ' on' : ''}`} aria-hidden>
          Fits
        </span>
        <span className={`oreview-stamp not${intent === 'reject' ? ' on' : ''}`} aria-hidden>
          Not me
        </span>

        <ReviewCard observation={current} entryTitle={entryTitle} />
      </div>

      <div className="oreview-acts">
        <button className="oreview-act reject" onClick={() => onVerdict('reject')} disabled={!!leaving}>
          Not me
        </button>
        <button className="oreview-act affirm" onClick={() => onVerdict('affirm')} disabled={!!leaving}>
          That fits
        </button>
      </div>

      <div className="oreview-alts">
        <button onClick={onDiscuss} disabled={!!leaving}>
          Talk it through
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => onVerdict('defer')} disabled={!!leaving}>
          Not now
        </button>
      </div>

      <p className="oreview-hint">Drag the card, or use ← and →. Swiping right means it fits.</p>
    </div>
  );
}
